import React, {useEffect, useState} from 'react';
import {Link, useLocation, useNavigate, useParams} from 'react-router-dom';
import Layout from '../components/Layout';
import {api} from '../../api';


const STATUS_COLORS = {
  NEW: 'bg-blue-100 text-blue-800',
  PENDING_FULFILLMENT: 'bg-yellow-100 text-yellow-800',
  FULFILLED: 'bg-green-100 text-green-800',
  CANCELLED: 'bg-red-100 text-red-800',
};

const CHANNEL_COLORS = {
  EBAY: 'bg-purple-100 text-purple-800',
  WEBSITE: 'bg-indigo-100 text-indigo-800',
  AMAZON: 'bg-orange-100 text-orange-800',
};

export default function OrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [order, setOrder] = useState(null);
  const [orderItems, setOrderItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState(location.state?.saved || false);
  const [showDelete, setShowDelete] = useState(false);
  const [deleteItemTarget, setDeleteItemTarget] = useState(null);

  // Add Item modal
  const [showAddItem, setShowAddItem] = useState(false);
  const [items, setItems] = useState([]);
  const [selectedItemId, setSelectedItemId] = useState('');
  const [addQuantity, setAddQuantity] = useState(1);
  const [addSize, setAddSize] = useState(null);
  const [addUnitId, setAddUnitId] = useState(null);
  const [availableInventory, setAvailableInventory] = useState({});
  const [availableSizes, setAvailableSizes] = useState([]);
  const [availableUnits, setAvailableUnits] = useState([]);
  const [step, setStep] = useState(0);

  // Edit Quantity modal
  const [editTarget, setEditTarget] = useState(null);
  const [editQuantity, setEditQuantity] = useState(1);
  const [editMax, setEditMax] = useState(1);
  const [editLoading, setEditLoading] = useState(false);

  useEffect(() => {
    if (saved) {
      const timer = setTimeout(() => setSaved(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [saved]);

  const fetchData = () => {
    Promise.all([
      api(`/orders/${id}`).then(res => res.ok ? res.json() : null),
      api(`/orders/${id}/order-items`).then(res => res.ok ? res.json() : []),
    ]).then(([orderData, orderItemsData]) => {
      setOrder(orderData);
      setOrderItems(Array.isArray(orderItemsData) ? orderItemsData : []);
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, [id]);

  const confirmDeleteOrder = () => {
    api(`/orders/${id}`, { method: 'DELETE' })
      .then(() => navigate('/inventory/orders'));
  };

  const confirmDeleteItem = () => {
    api(`/orders/${id}/order-items/${deleteItemTarget}`, { method: 'DELETE' })
      .then(() => { setDeleteItemTarget(null); fetchData(); });
  };

  // --- Add Item ---

  const openAddItem = () => {
    api(`/items`).then(res => res.json()).then(data => {
      const allItems = Array.isArray(data) ? data : [];
      Promise.all(allItems.map(item =>
        api(`/items/${item.id}/inventory?availability=AVAILABLE`).then(res => res.ok ? res.json() : [])
      )).then(inventories => {
        const itemsWithAvailable = [];
        const invMap = {};
        allItems.forEach((item, idx) => {
          const available = inventories[idx] || [];
          if (available.length > 0) {
            itemsWithAvailable.push(item);
            invMap[item.id] = available;
          }
        });
        setItems(itemsWithAvailable);
        setAvailableInventory(invMap);
        setSelectedItemId('');
        setAddQuantity(1);
        setAddSize(null);
        setAddUnitId(null);
        setAvailableSizes([]);
        setAvailableUnits([]);
        setStep(0);
        setShowAddItem(true);
      });
    });
  };

  const handleItemSelect = (itemId) => {
    setSelectedItemId(itemId);
    setAddUnitId(null);
    setAddSize(null);
    setAddQuantity(1);
    setAvailableSizes([]);
    setAvailableUnits([]);
    setStep(0);
    if (!itemId || !availableInventory[itemId]) return;
    const inv = availableInventory[itemId];
    const unitMap = new Map();
    inv.forEach(i => {
      const uid = i.unitId || '';
      if (!unitMap.has(uid)) unitMap.set(uid, i.unitName || '');
    });
    setAvailableUnits(Array.from(unitMap, ([uid, name]) => ({ id: uid, name: name || 'No Unit' })));
    setStep(1);
  };

  const handleUnitSelect = (val) => {
    const unitId = val === 'NONE' ? '' : val;
    setAddUnitId(unitId);
    setAddSize(null);
    setAddQuantity(1);
    const inv = availableInventory[selectedItemId];
    if (!inv) return;
    const filtered = inv.filter(i => (i.unitId || '') === unitId);
    const sizeSet = new Set();
    filtered.forEach(i => sizeSet.add(i.size != null ? i.size : ''));
    setAvailableSizes(Array.from(sizeSet).sort((a, b) => {
      if (a === '') return -1;
      if (b === '') return 1;
      return a.localeCompare(b);
    }));
    setStep(2);
  };

  const handleSizeSelect = (val) => {
    const size = val === 'NONE' ? '' : val;
    setAddSize(size);
    setAddQuantity(1);
    setStep(3);
  };

  const getMaxQuantity = () => {
    if (!selectedItemId || !availableInventory[selectedItemId] || addUnitId === null || addSize === null) return 1;
    return availableInventory[selectedItemId]
      .filter(i => (i.unitId || '') === addUnitId && (i.size || '') === addSize)
      .length || 1;
  };

  const handleAddItem = (e) => {
    e.preventDefault();
    const quantity = addQuantity === '' ? 1 : parseInt(addQuantity);
    api(`/orders/${id}/order-items`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ itemId: selectedItemId, quantity: quantity, size: addSize || undefined, unitId: addUnitId || undefined }),
    }).then(() => { setShowAddItem(false); fetchData(); });
  };

  // --- Edit Quantity ---

  const openEditQuantity = (oi) => {
    setEditLoading(true);
    setEditTarget(oi);
    setEditQuantity(oi.quantity);
    api(`/items/${oi.itemId}/inventory?availability=AVAILABLE`).then(res => res.ok ? res.json() : []).then(inv => {
      const matching = inv.filter(i => (i.unitId || '') === (oi.unitId || '') && (i.size || '') === (oi.size || ''));
      setEditMax(matching.length + oi.quantity);
      setEditLoading(false);
    });
  };

  const handleEditQuantity = (e) => {
    e.preventDefault();
    const quantity = editQuantity === '' ? 1 : parseInt(editQuantity);
    api(`/orders/${id}/order-items/${editTarget.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ itemId: editTarget.itemId, quantity: quantity, size: editTarget.size || undefined, unitId: editTarget.unitId || undefined }),
    }).then(() => { setEditTarget(null); fetchData(); });
  };

  if (loading) return <div className="p-6">Loading...</div>;
  if (!order) return <div className="p-6">Order not found.</div>;

  return (
    <Layout>
          {saved && (
            <div className="mb-4 px-4 py-3 bg-green-100 text-green-800 rounded-lg">Order saved successfully.</div>
          )}
          <Link to="/inventory/orders" className="text-red-600 hover:text-red-800 mb-4 inline-block">&larr; Back to Orders</Link>
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
              <h1 className="text-2xl font-bold text-gray-900">Order Details</h1>
              <div className="flex gap-3">
                <Link to={`/inventory/orders/${id}/edit`} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
                  Edit
                </Link>
                <button onClick={() => setShowDelete(true)} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">
                  Delete
                </button>
              </div>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-500">External Order ID</label>
              <p className="mt-1 text-gray-900">{order.externalOrderId || '—'}</p>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-500">Channel</label>
              <p className="mt-1">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${CHANNEL_COLORS[order.channel] || 'bg-gray-100 text-gray-800'}`}>
                  {order.channel}
                </span>
              </p>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-500">Status</label>
              <p className="mt-1">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${STATUS_COLORS[order.status] || 'bg-gray-100 text-gray-800'}`}>
                  {order.status.replace(/_/g, ' ')}
                </span>
              </p>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-500">Created</label>
              <p className="mt-1 text-gray-900">{new Date(order.createdAt).toLocaleString()}</p>
            </div>
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-500">Updated</label>
              <p className="mt-1 text-gray-900">{new Date(order.updatedAt).toLocaleString()}</p>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow">
            <div className="p-6 border-b">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold text-gray-900">Items ({orderItems.length})</h2>
                <button onClick={openAddItem} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">
                  Add Item
                </button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Item Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">SKU</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Unit</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Size</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Quantity</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {orderItems.map((oi) => (
                    <tr key={oi.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">
                        <Link to={`/inventory/items/${oi.itemId}`} className="text-red-600 hover:text-red-800">{oi.itemName}</Link>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-600">{oi.itemSku || '—'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-900">{oi.unitName || '—'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-900">{oi.size || '—'}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button onClick={() => openEditQuantity(oi)} className="text-red-600 hover:text-red-800 font-medium">{oi.quantity}</button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button onClick={() => setDeleteItemTarget(oi.id)} className="text-gray-500 hover:text-red-600 font-medium">Remove</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {orderItems.length === 0 && (
                <div className="text-center py-12 text-gray-500">No items added to this order yet.</div>
              )}
            </div>
          </div>

          {showDelete && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg shadow-lg p-6 w-96">
                <h3 className="text-lg font-bold text-gray-900 mb-2">Confirm Delete</h3>
                <p className="text-gray-600 mb-6">Are you sure you want to delete this order?</p>
                <div className="flex justify-end gap-3">
                  <button onClick={() => setShowDelete(false)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
                  <button onClick={confirmDeleteOrder} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">Delete</button>
                </div>
              </div>
            </div>
          )}

          {deleteItemTarget && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg shadow-lg p-6 w-96">
                <h3 className="text-lg font-bold text-gray-900 mb-2">Confirm Remove</h3>
                <p className="text-gray-600 mb-6">Are you sure you want to remove this item from the order?</p>
                <div className="flex justify-end gap-3">
                  <button onClick={() => setDeleteItemTarget(null)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
                  <button onClick={confirmDeleteItem} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">Remove</button>
                </div>
              </div>
            </div>
          )}

          {editTarget && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg shadow-lg p-6 w-96">
                <h3 className="text-lg font-bold text-gray-900 mb-2">Edit Quantity</h3>
                <p className="text-sm text-gray-600 mb-4">{editTarget.itemName}{editTarget.unitName ? ` · ${editTarget.unitName}` : ''}{editTarget.size ? ` · ${editTarget.size}` : ''}</p>
                {editLoading ? <p className="text-gray-500 mb-4">Checking availability...</p> : (
                  <form onSubmit={handleEditQuantity}>
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Quantity (max {editMax})</label>
                      <input
                        type="number"
                        max={editMax}
                        required
                        value={editQuantity}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '') {
                            setEditQuantity('');
                          } else {
                            const num = parseInt(value);
                            if (!isNaN(num)) {
                              setEditQuantity(Math.min(Math.max(num, 1), editMax));
                            }
                          }
                        }}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600"
                      />
                    </div>
                    <div className="flex justify-end gap-3">
                      <button type="button" onClick={() => setEditTarget(null)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
                      <button type="submit" className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">Save</button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          )}

          {showAddItem && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg shadow-lg p-6 w-96">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Add Item to Order</h3>
                <form onSubmit={handleAddItem}>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Item</label>
                    <select
                      required
                      value={selectedItemId}
                      onChange={(e) => handleItemSelect(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600"
                    >
                      <option value="">Select an item...</option>
                      {items.map((item) => (
                        <option key={item.id} value={item.id}>{item.name}{item.sku ? ` (${item.sku})` : ''}</option>
                      ))}
                    </select>
                  </div>
                  {step >= 1 && (
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Unit</label>
                      <select
                        required
                        value={addUnitId === null ? '' : (addUnitId === '' ? 'NONE' : addUnitId)}
                        onChange={(e) => handleUnitSelect(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600"
                      >
                        <option value="">Select a unit...</option>
                        {availableUnits.map(u => (
                          <option key={u.id || 'NONE'} value={u.id || 'NONE'}>{u.name}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  {step >= 2 && (
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Size</label>
                      <select
                        required
                        value={addSize === null ? '' : (addSize === '' ? 'NONE' : addSize)}
                        onChange={(e) => handleSizeSelect(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600"
                      >
                        <option value="">Select a size...</option>
                        {availableSizes.map(s => (
                          <option key={s || 'NONE'} value={s || 'NONE'}>{s || 'No Size'}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  {step >= 3 && (
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Quantity (max {getMaxQuantity()})</label>
                      <input
                        type="number"
                        max={getMaxQuantity()}
                        required
                        value={addQuantity}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '') {
                            setAddQuantity('');
                          } else {
                            const num = parseInt(value);
                            if (!isNaN(num)) {
                              setAddQuantity(Math.min(Math.max(num, 1), getMaxQuantity()));
                            }
                          }
                        }}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600"
                      />
                    </div>
                  )}
                  <div className="flex justify-end gap-3">
                    <button type="button" onClick={() => setShowAddItem(false)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
                    <button type="submit" disabled={step < 3} className={`px-4 py-2 rounded-lg ${step >= 3 ? 'bg-red-600 text-white hover:bg-red-700' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`}>Add</button>
                  </div>
                </form>
              </div>
            </div>
          )}
    </Layout>
  );
}
